import { Button } from "@/components/ui/button";
import {
  useDeleteDepartmentOverride,
  useUpsertDepartmentOverride,
} from "@/hooks/useSupportDepartments";
import type { Department, DepartmentMember } from "@/services/supportDepartmentsService";

import { apiErrorMessage } from "./slackId";

/** Core overrides on a Teamwork member: make lead (a 'lead' override, which
 *  only counts while they hold the Teamwork role) / undo lead, and remove from
 *  the department. Needs an email (the override key) and an active member. A
 *  Teamwork lead has no lead toggle: Core can add a lead, not take one away.
 *  Where an action is unavailable, a short hint says why. */
export function MemberOverrideActions({
  member,
  department,
}: {
  member: DepartmentMember;
  department: Department;
}) {
  const upsert = useUpsertDepartmentOverride();
  const remove = useDeleteDepartmentOverride();

  if (!member.is_active) return null;
  if (!member.email) {
    return (
      <p className="text-xs text-muted-foreground">
        No email in Teamwork, so Core can't change this person.
      </p>
    );
  }
  const email = member.email;
  const busy = upsert.isPending || remove.isPending;
  const error = upsert.error ?? remove.error;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!member.is_lead ? (
        member.lead_in_core ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            aria-label={`Remove ${member.name} as lead`}
            onClick={() => remove.mutate({ department: department.key, email })}
          >
            Remove lead
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            aria-label={`Make ${member.name} lead`}
            onClick={() =>
              upsert.mutate({
                department: department.key,
                input: { email, display_name: member.name, mode: "lead" },
              })
            }
          >
            Make lead
          </Button>
        )
      ) : (
        <p className="text-xs text-muted-foreground">
          Lead set in Teamwork; change it there.
        </p>
      )}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={busy}
        aria-label={`Remove ${member.name} from ${department.label}`}
        onClick={() =>
          upsert.mutate({
            department: department.key,
            input: { email, display_name: member.name, mode: "exclude" },
          })
        }
      >
        Remove
      </Button>
      {error ? (
        <p role="alert" className="w-full text-xs text-destructive">
          {apiErrorMessage(error, "the change was not saved.")}
        </p>
      ) : null}
    </div>
  );
}
